'use client'

import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import Header from '@/components/Header'
import HeroParallax from '@/components/HeroParallax'
import Footer from '@/components/Footer'
import { getExperienceById, experiences } from '@/lib/data'
import BookNowCTA from '@/components/BookNowCTA'
import styles from '@/styles/experience-page.module.css'
import { BreadcrumbSchema } from '@/lib/schema'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import '@/components/Adventures.css'

export default function WineRoutePage() {
  const thisExperience = getExperienceById('wine-route')
  const t = useTranslations('experiences')
  const tCommon = useTranslations('common')
  const params = useParams()
  const locale = params.locale as string

  const relatedExperiences = experiences.filter(e => e.id !== 'wine-route').slice(0, 2)

  const mainRef = useScrollAnimation<HTMLElement>({
    threshold: 0.12,
    rootMargin: '0px 0px -60px 0px',
  })

  if (!thisExperience) {
    return <div>{t('common.experienceNotFound')}</div>
  }

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: t('index.title'), href: `/${locale}/experiences/` },
        { name: thisExperience.title, href: `/${locale}/experiences/wine-route/` },
      ]} />
      <Header />

      {/* Hero 100vh */}
      <HeroParallax
        backgroundImage={thisExperience.heroImage}
        title={thisExperience.title}
        subtitle={thisExperience.subtitle}
        location={thisExperience.location}
      />

      <main className={styles.pageWrapper} ref={mainRef}>
        {/* Experience Details Section */}
        <section className={styles.experienceDetails}>
          <div className="container">
            <div className={styles.detailsGrid}>
              {/* Left Column: Highlights (Vineyards to visit) */}
              <div className={styles.highlightsColumn} data-scroll-item>
                <h3>{t('wineRoute.highlightsTitle')}</h3>
                <p className={styles.duration}>{t('wineRoute.duration')}</p>

                <div className={styles.highlightItem} data-scroll-item style={{ transitionDelay: '120ms' }}>
                  <strong>{t('wineRoute.highlight1Title')}</strong>
                  <p>{t('wineRoute.highlight1')}</p>
                </div>

                <div className={styles.highlightItem} data-scroll-item style={{ transitionDelay: '200ms' }}>
                  <strong>{t('wineRoute.highlight2Title')}</strong>
                  <p>{t('wineRoute.highlight2')}</p>
                </div>

                <div className={styles.highlightItem} data-scroll-item style={{ transitionDelay: '280ms' }}>
                  <strong>{t('wineRoute.highlight3Title')}</strong>
                  <p>{t('wineRoute.highlight3')}</p>
                </div>

                <div className={styles.highlightItem} data-scroll-item style={{ transitionDelay: '360ms' }}>
                  <strong>{t('wineRoute.highlight4Title')}</strong>
                  <p>{t('wineRoute.highlight4')}</p>
                </div>
              </div>

              {/* Center Column: Feature Image */}
              <div className={styles.featureColumn} data-scroll-item style={{ transitionDelay: '150ms' }}>
                <div className={styles.featureWrapper}>
                  <img
                    src="/images/experiences/wine/wine-feature.webp"
                    alt={t('wineRoute.alts.feature')}
                    className={styles.featureImage}
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Right Column: What's Included */}
              <div className={styles.includedColumn} data-scroll-item style={{ transitionDelay: '300ms' }}>
                <h3>{t('common.whatsIncluded')}</h3>
                <ul>
                  <li>{t('wineRoute.included.item1')}</li>
                  <li>{t('wineRoute.included.item2')}</li>
                  <li>{t('wineRoute.included.item3')}</li>
                  <li>{t('wineRoute.included.item4')}</li>
                  <li>{t('wineRoute.included.item5')}</li>
                  <li>{t('wineRoute.included.item6')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div data-scroll-item>
          <BookNowCTA
            variant="inline"
            productId="wine-route"
            productType="experience"
            productName={t('wineRoute.productName')}
            bookingMode={thisExperience.bookingMode || 'whatsapp'}
          />
        </div>

        {/* Optional Experiences Section */}
        <section className={styles.optionalSection}>
          <div className="container">
            <div className={styles.optionalHeader} data-scroll-item>
              <h2>{t('wineRoute.optionalTitle')}</h2>
              <p>{t('wineRoute.optionalDescription')}</p>
            </div>

            <div className={styles.optionalGridCentered}>
              <div className={styles.optionalCard} data-scroll-item style={{ transitionDelay: '0ms' }}>
                <div className={styles.optionalCardIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                  </svg>
                </div>
                <h4>{t('wineRoute.optional.item1Title')}</h4>
                <p>{t('wineRoute.optional.item1')}</p>
              </div>

              <div className={styles.optionalCard} data-scroll-item style={{ transitionDelay: '100ms' }}>
                <div className={styles.optionalCardIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.87c1.355 0 2.697.055 4.024.165C17.155 8.51 18 9.473 18 10.608v2.513m-3-4.87v-1.5m-6 1.5v-1.5m12 9.75l-1.5.75a3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0L3 16.5m15-3.38a48.474 48.474 0 00-6-.37c-2.032 0-4.034.125-6 .37m12 0c.39.049.777.102 1.163.16 1.07.16 1.837 1.094 1.837 2.175v5.17c0 .62-.504 1.124-1.125 1.124H4.125A1.125 1.125 0 013 20.625v-5.17c0-1.08.768-2.014 1.837-2.174A47.78 47.78 0 016 13.12" />
                  </svg>
                </div>
                <h4>{t('wineRoute.optional.item2Title')}</h4>
                <p>{t('wineRoute.optional.item2')}</p>
              </div>

              <div className={styles.optionalCard} data-scroll-item style={{ transitionDelay: '200ms' }}>
                <div className={styles.optionalCardIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                  </svg>
                </div>
                <h4>{t('wineRoute.optional.item3Title')}</h4>
                <p>{t('wineRoute.optional.item3')}</p>
              </div>

              <div className={styles.optionalCard} data-scroll-item style={{ transitionDelay: '300ms' }}>
                <div className={styles.optionalCardIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                  </svg>
                </div>
                <h4>{t('wineRoute.optional.item4Title')}</h4>
                <p>{t('wineRoute.optional.item4')}</p>
              </div>

              <div className={styles.optionalCard} data-scroll-item style={{ transitionDelay: '400ms' }}>
                <div className={styles.optionalCardIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                  </svg>
                </div>
                <h4>{t('wineRoute.optional.item5Title')}</h4>
                <p>{t('wineRoute.optional.item5')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className={styles.gallery}>
          <div className="container">
            <div className={styles.sectionHeader} data-scroll-item>
              <h2 className={styles.sectionTitle}>{t('wineRoute.galleryTitle')}</h2>
              <p className={styles.sectionDescription}>
                {t('wineRoute.galleryDescription')}
              </p>
            </div>

            <div className={styles.galleryGrid}>
              {[
                { src: '/images/experiences/wine/wine-gallery-01.webp', alt: t('wineRoute.alts.gallery01') },
                { src: '/images/experiences/wine/wine-gallery-02.webp', alt: t('wineRoute.alts.gallery02') },
                { src: '/images/experiences/wine/wine-gallery-03.webp', alt: t('wineRoute.alts.gallery03') },
                { src: '/images/experiences/wine/wine-gallery-04.webp', alt: t('wineRoute.alts.gallery04') },
                { src: '/images/experiences/wine/wine-gallery-05.webp', alt: t('wineRoute.alts.gallery05') },
                { src: '/images/experiences/wine/wine-gallery-06.webp', alt: t('wineRoute.alts.gallery06') },
              ].map((image, idx) => (
                <div key={idx} className={styles.galleryItem} data-scroll-item style={{ transitionDelay: `${idx * 80}ms` }}>
                  <img src={image.src} alt={image.alt} loading="lazy" />
                </div>
              ))}
            </div>

            <div className={styles.sectionCta} data-scroll-item>
              <a href={`/${locale}/experiences/`} className="btn-primary">
                {t('common.exploreExperiences')} &rarr;
              </a>
            </div>
          </div>
        </section>

        {/* Related Experiences Section */}
        <section className={styles.relatedSection}>
          <div className="container">
            <h2 data-scroll-item>{t('common.relatedExperiences')}</h2>
            <div className={styles.relatedGrid}>
              {relatedExperiences.map((experience, i) => (
                <article key={experience.id} className={styles.relatedCard} data-scroll-item style={{ transitionDelay: `${120 + i * 120}ms` }}>
                  <div className={styles.relatedImage}>
                    <img
                      src={experience.heroImage}
                      alt={experience.title}
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.relatedOverlay}>
                    <h3 className={styles.relatedTitle}>{experience.title}</h3>
                    <div className={styles.relatedContent}>
                      <p>{experience.subtitle}</p>
                      <a href={`/${locale}/experiences/${experience.id}/`} className={styles.relatedLink}>
                        {t('common.explore')} &rarr;
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <div data-scroll-item>
          <BookNowCTA
            variant="closing"
            productId="wine-route"
            productType="experience"
            productName={t('wineRoute.productName')}
            bookingMode={thisExperience.bookingMode || 'whatsapp'}
          />
        </div>
      </main>

      <Footer />
    </>
  )
}
